import React from 'react';
import { useAppStore } from '../store/app-store';
import { getAllTextModels, getAllEmbeddingModels } from '../config/cloudflare-models';

export function Settings() {
  const { settings, updateSettings, toggleSettings } = useAppStore();

  const textModels = getAllTextModels();
  const embeddingModels = getAllEmbeddingModels();

  const handleChange = (key, value) => {
    updateSettings({ [key]: value });
  };

  return (
    <div className="settings-panel">
      <div className="settings-header">
        <h2>Settings</h2>
        <button className="close-btn" onClick={toggleSettings}>×</button>
      </div>

      <div className="settings-content">
        {/* Auto-Compaction Settings */}
        <section className="settings-section">
          <h3>Auto-Compaction</h3>
          <p className="section-description">
            Automatically compress conversation history to save tokens
          </p>

          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.autoCompact}
                onChange={(e) => handleChange('autoCompact', e.target.checked)}
              />
              Enable Auto-Compaction
            </label>
          </div>

          <div className="setting-item">
            <label>
              Compaction Threshold (tokens)
              <input
                type="number"
                value={settings.compactionThreshold}
                onChange={(e) => handleChange('compactionThreshold', parseInt(e.target.value))}
                min="10000"
                max="500000"
                step="10000"
              />
            </label>
            <span className="setting-hint">Trigger compaction when conversation exceeds this size</span>
          </div>

          <div className="setting-item">
            <label>
              Compaction Target (tokens)
              <input
                type="number"
                value={settings.compactionTarget}
                onChange={(e) => handleChange('compactionTarget', parseInt(e.target.value))}
                min="5000"
                max="100000"
                step="5000"
              />
            </label>
            <span className="setting-hint">Target size after compaction</span>
          </div>
        </section>

        {/* Memory Settings */}
        <section className="settings-section">
          <h3>Memory & Context</h3>
          <p className="section-description">
            Manage conversation memory and semantic search
          </p>

          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.enableMemory}
                onChange={(e) => handleChange('enableMemory', e.target.checked)}
              />
              Enable Memory System
            </label>
          </div>

          <div className="setting-item">
            <label>
              Max Memory Items
              <input
                type="number"
                value={settings.maxMemoryItems}
                onChange={(e) => handleChange('maxMemoryItems', parseInt(e.target.value))}
                min="100"
                max="10000"
                step="100"
              />
            </label>
            <span className="setting-hint">Maximum number of memories to store</span>
          </div>

          <div className="setting-item">
            <label>
              Similarity Threshold
              <input
                type="range"
                value={settings.similarityThreshold}
                onChange={(e) => handleChange('similarityThreshold', parseFloat(e.target.value))}
                min="0"
                max="1"
                step="0.05"
              />
              <span className="value">{settings.similarityThreshold.toFixed(2)}</span>
            </label>
            <span className="setting-hint">Minimum similarity for memory retrieval (0-1)</span>
          </div>
        </section>

        {/* Agent Settings */}
        <section className="settings-section">
          <h3>Agents</h3>
          <p className="section-description">
            Configure automatic agent creation and orchestration
          </p>

          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.autoCreateAgents}
                onChange={(e) => handleChange('autoCreateAgents', e.target.checked)}
              />
              Auto-Create Specialized Agents
            </label>
            <span className="setting-hint">Automatically create agents based on task type</span>
          </div>

          <div className="setting-item">
            <label>
              Max Concurrent Agents
              <input
                type="number"
                value={settings.maxConcurrentAgents}
                onChange={(e) => handleChange('maxConcurrentAgents', parseInt(e.target.value))}
                min="1"
                max="20"
              />
            </label>
            <span className="setting-hint">Maximum agents running simultaneously</span>
          </div>

          <div className="setting-item">
            <label>
              Agent Timeout (ms)
              <input
                type="number"
                value={settings.agentTimeout}
                onChange={(e) => handleChange('agentTimeout', parseInt(e.target.value))}
                min="30000"
                max="600000"
                step="30000"
              />
            </label>
            <span className="setting-hint">Maximum time for agent execution</span>
          </div>
        </section>

        {/* Model Settings */}
        <section className="settings-section">
          <h3>Models</h3>
          <p className="section-description">
            Select Cloudflare Workers AI models for different tasks
          </p>

          <div className="setting-item">
            <label>
              Default Model
              <select
                value={settings.defaultModel}
                onChange={(e) => handleChange('defaultModel', e.target.value)}
              >
                {textModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </label>
            <span className="setting-hint">Primary model for complex tasks</span>
          </div>

          <div className="setting-item">
            <label>
              Fast Model
              <select
                value={settings.fastModel}
                onChange={(e) => handleChange('fastModel', e.target.value)}
              >
                {textModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </label>
            <span className="setting-hint">Model for quick responses</span>
          </div>

          <div className="setting-item">
            <label>
              Coding Model
              <select
                value={settings.codingModel}
                onChange={(e) => handleChange('codingModel', e.target.value)}
              >
                {textModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </label>
            <span className="setting-hint">Specialized model for code generation</span>
          </div>

          <div className="setting-item">
            <label>
              Agent Model
              <select
                value={settings.agentModel}
                onChange={(e) => handleChange('agentModel', e.target.value)}
              >
                {textModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </label>
            <span className="setting-hint">Model for agent orchestration</span>
          </div>

          <div className="setting-item">
            <label>
              Embedding Model
              <select
                value={settings.embeddingModel}
                onChange={(e) => handleChange('embeddingModel', e.target.value)}
              >
                {embeddingModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </label>
            <span className="setting-hint">Model for semantic search and memory</span>
          </div>
        </section>

        {/* Generation Settings */}
        <section className="settings-section">
          <h3>Generation Parameters</h3>

          <div className="setting-item">
            <label>
              Temperature
              <input
                type="range"
                value={settings.temperature}
                onChange={(e) => handleChange('temperature', parseFloat(e.target.value))}
                min="0"
                max="2"
                step="0.1"
              />
              <span className="value">{settings.temperature.toFixed(1)}</span>
            </label>
            <span className="setting-hint">Randomness of responses (0 = deterministic, 2 = very random)</span>
          </div>

          <div className="setting-item">
            <label>
              Max Tokens
              <input
                type="number"
                value={settings.maxTokens}
                onChange={(e) => handleChange('maxTokens', parseInt(e.target.value))}
                min="100"
                max="32000"
                step="100"
              />
            </label>
            <span className="setting-hint">Maximum tokens per response</span>
          </div>

          <div className="setting-item">
            <label>
              Top P
              <input
                type="range"
                value={settings.topP}
                onChange={(e) => handleChange('topP', parseFloat(e.target.value))}
                min="0"
                max="1"
                step="0.05"
              />
              <span className="value">{settings.topP.toFixed(2)}</span>
            </label>
            <span className="setting-hint">Nucleus sampling threshold</span>
          </div>
        </section>

        {/* UI Settings */}
        <section className="settings-section">
          <h3>Interface</h3>

          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.codeHighlighting}
                onChange={(e) => handleChange('codeHighlighting', e.target.checked)}
              />
              Syntax Highlighting
            </label>
          </div>

          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.streamingResponses}
                onChange={(e) => handleChange('streamingResponses', e.target.checked)}
              />
              Streaming Responses
            </label>
            <span className="setting-hint">Display responses as they're generated</span>
          </div>

          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.showTokenCount}
                onChange={(e) => handleChange('showTokenCount', e.target.checked)}
              />
              Show Token Count
            </label>
          </div>
        </section>
      </div>
    </div>
  );
}
